/**
 * retryConfig — named, per-tier retry/backoff strategies for `useApiQuery`
 * (A5-438).
 *
 * Before this, every query used TanStack Query's defaults (3 retries, built-in
 * exponential backoff capped at ~30 s) with no way to differentiate:
 *   - a transient 503 from a PLC/edge sync delay (worth a few quick retries),
 *   - a slow historian aggregation (already long; retrying just piles on load),
 *   - a regulated write/read that must surface the error immediately to the
 *     operator rather than silently retry.
 *
 * A strategy is a small, declarative bag: how many retries, the initial/max
 * backoff delay, and a `shouldRetry(failureCount, error)` predicate that can
 * veto by status code. {@link buildRetryOptions} compiles a strategy into the
 * `{ retry, retryDelay }` pair TanStack consumes, and `useApiQueryWithRetry`
 * (in useApi.ts) accepts a strategy by name.
 *
 * All delays use full-jitter exponential backoff: `random(0, min(max, init * 2^n))`.
 * Jitter spreads a thundering-herd of retries (e.g. 50 tiles all hitting a
 * recovering gateway) instead of synchronising their retry waves.
 */

/** A retryable error carrying an optional HTTP status. */
export interface RetryableError {
  statusCode?: number;
  status?: number;
  code?: string;
}

/** Declarative retry/backoff policy. */
export interface RetryConfig {
  /** Maximum number of retries AFTER the initial attempt. */
  maxRetries: number;
  /** Initial backoff delay in ms (the `init` in `init * 2^n`). */
  initialDelayMs: number;
  /** Upper bound on a single backoff delay in ms. */
  maxDelayMs: number;
  /**
   * Predicate deciding whether to retry. `failureCount` is 1-based (1 after the
   * first failure). Return false to stop early regardless of `maxRetries`.
   */
  shouldRetry?: (failureCount: number, error: unknown) => boolean;
}

/** Read an HTTP status off whatever error shape the API layer threw. */
export const statusOf = (error: unknown): number | undefined => {
  if (error && typeof error === 'object') {
    const e = error as RetryableError;
    if (typeof e.statusCode === 'number') return e.statusCode;
    if (typeof e.status === 'number') return e.status;
  }
  return undefined;
};

/**
 * Default retry predicate: retry transient/network failures, never retry a
 * deterministic 4xx (a 400/404/409/422 won't fix itself), and never retry
 * 401/403 (the auth layer already handled refresh + logout). 408 (timeout),
 * 425, 429 and any 5xx are treated as transient.
 */
export const isTransient = (error: unknown): boolean => {
  const status = statusOf(error);
  if (status === undefined) return true; // network error / no response
  if (status === 401 || status === 403) return false;
  if (status === 408 || status === 425 || status === 429) return true;
  if (status >= 500) return true;
  return false; // other 4xx — deterministic, do not retry
};

/**
 * Compute a full-jitter exponential backoff delay for a given retry attempt.
 * `attempt` is 1-based (1 for the first retry).
 */
export const backoffDelay = (attempt: number, config: RetryConfig): number => {
  const exp = config.initialDelayMs * 2 ** Math.max(0, attempt - 1);
  const capped = Math.min(config.maxDelayMs, exp);
  return Math.floor(Math.random() * capped);
};

/**
 * Named, per-tier defaults. Pick by the latency/failure profile of the endpoint:
 *
 *  - `realtime` — live tiles / PLC-backed reads. A 503 here is usually a brief
 *    edge-sync gap; retry quickly a few times.
 *  - `standard` — ordinary CRUD reads. Balanced defaults.
 *  - `historian` — slow aggregation queries. Already long; retry once, slowly,
 *    so we don't multiply load on a struggling time-series store.
 *  - `regulated` — GxP reads/writes that must surface failure to the operator
 *    immediately. No silent retries.
 */
export const RETRY_STRATEGIES = {
  realtime: {
    maxRetries: 4,
    initialDelayMs: 250,
    maxDelayMs: 4_000,
    shouldRetry: (count, error) => count <= 4 && isTransient(error),
  },
  standard: {
    maxRetries: 3,
    initialDelayMs: 500,
    maxDelayMs: 15_000,
    shouldRetry: (count, error) => count <= 3 && isTransient(error),
  },
  historian: {
    maxRetries: 1,
    initialDelayMs: 2_000,
    maxDelayMs: 10_000,
    shouldRetry: (count, error) => count <= 1 && isTransient(error),
  },
  regulated: {
    maxRetries: 0,
    initialDelayMs: 0,
    maxDelayMs: 0,
    shouldRetry: () => false,
  },
} as const satisfies Record<string, RetryConfig>;

/** Name of a built-in strategy. */
export type RetryStrategyName = keyof typeof RETRY_STRATEGIES;

/** Resolve a strategy by name, or pass a custom config through unchanged. */
export const resolveRetryConfig = (
  strategy: RetryStrategyName | RetryConfig,
): RetryConfig =>
  typeof strategy === 'string' ? RETRY_STRATEGIES[strategy] : strategy;

/**
 * Compile a retry strategy into the `{ retry, retryDelay }` options TanStack
 * Query consumes. `retry` is the failureCount predicate; `retryDelay` returns
 * the jittered backoff for each attempt.
 */
export const buildRetryOptions = (
  strategy: RetryStrategyName | RetryConfig,
): {
  retry: (failureCount: number, error: unknown) => boolean;
  retryDelay: (attempt: number) => number;
} => {
  const config = resolveRetryConfig(strategy);
  const predicate = config.shouldRetry ?? ((count, error) => count <= config.maxRetries && isTransient(error));
  return {
    retry: (failureCount, error) =>
      failureCount <= config.maxRetries && predicate(failureCount, error),
    // TanStack passes a 0-based attempt index; backoffDelay is 1-based.
    retryDelay: (attempt) => backoffDelay(attempt + 1, config),
  };
};
